import {Dispatch, SetStateAction} from 'react';
import {NodeProps} from 'reactflow';

import {DecisionTreeNodeDataFrontChat} from '../../../../libs/shared/core/src/models/api/decisionTreeNodeDataModel';
import {MutateTreeReturn} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {
  BranchNodeToDelete,
  DecisionTreeNode,
  DecisionTreeStore,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {ChatBotExpressionsEditor} from './expressions/expressionHelpers';

/*
 * Types.
 */

export interface ChatBotBuilderFeatureFlags {
  hasChatbotArchiveAction: boolean;
  hasChatbotsTagNode: boolean;
  hasChatbotsParseTextNode: boolean;
  // This is an OR between the `ai_answers` feature and the `semantic-search-node` feature flag.
  hasAiAnswersStep: boolean;
  hasSubtreeNode: boolean;
  hasChatbotDynamicVariables: boolean;
}

export enum ChatBotBuilderValidationErrorsEnum {
  EMPTY_MESSAGE = 'EMPTY_MESSAGE',
  MISSING_OPTIONS = 'MISSING_OPTIONS',
  MESSAGE_TOO_LONG = 'MESSAGE_TOO_LONG',
  TOO_MANY_KEYWORDS = 'TOO_MANY_KEYWORDS',
  EMPTY_TAGS = 'EMPTY_TAGS',
  EXPRESSION_TYPE_CHANGED = 'EXPRESSION_TYPE_CHANGED',
}

export interface ChatBotBuilderValidation {
  isValid: boolean;
  validationErrors: ChatBotBuilderValidationErrorsEnum[];
}

/**
 * The React Flow "data" field we control on each of our nodes.
 */
export type ChatBotBuilderNodeData = DecisionTreeNodeDataFrontChat;

export type ChatBotBuilderNode = DecisionTreeNode<ChatBotBuilderNodeData>;

export type ChatBotBuilderNodeProps = NodeProps<ChatBotBuilderNodeData>;

export type SetNodesAndDirty = (arg: Array<ChatBotBuilderNode>) => void;

export type UpdateNodeAndDirty = (id: string, data: ChatBotBuilderNodeData) => ChatBotBuilderNode | undefined;

export type UpdateNodesAndDirty = (
  nodesToUpdate: Array<Pick<ChatBotBuilderNode, 'id' | 'data'>>,
) => MutateTreeReturn<ChatBotBuilderNodeData>;

export type DeleteNodeAndDirty = (node: DecisionTreeNode<DecisionTreeNodeDataFrontChat>) => void;

export type DeleteNodePreserveBranchAndDirty = (nodeId: string, preserveBranchSourceHandle: string) => void;
export type DeleteNodesPreserveBranchAndDirty = (nodesToDelete: ReadonlyArray<BranchNodeToDelete>) => void;
export type DeletePromptNodeAndDirty = (nodeId: string) => void;

/**
 * The context of the currently selected node, which has its detailed displayed in the right-panel of the builder.
 */

export interface SelectedNodeOptions {
  shouldAddOption?: boolean;
  shouldUndoOnCancel?: boolean;
}

export type SelectedNode = {
  node: ChatBotBuilderNode;
  options?: SelectedNodeOptions;
};

/**
 * The Decision Tree node and edge controlled states and helpers for their management.
 */
export type ChatBotBuilderStore = DecisionTreeStore<ChatBotBuilderNodeData> & {
  setNodesAndDirty: SetNodesAndDirty;
  updateNodeAndDirty: UpdateNodeAndDirty;
  updateNodesAndDirty: UpdateNodesAndDirty;
  deleteNodeAndDirty: DeleteNodeAndDirty;
  deleteNodePreserveBranchAndDirty: DeleteNodePreserveBranchAndDirty;
  deleteNodesPreserveBranchAndDirty: DeleteNodesPreserveBranchAndDirty;
  deletePromptNodeAndDirty: DeletePromptNodeAndDirty;
  undo: () => void;
  selectedNode: SelectedNode | undefined;
  setSelectedNode: (arg: SelectedNode | undefined) => void;
  nodeIdsWithErrors: ReadonlyArray<string>;
  setNodeIdsWithErrors: Dispatch<SetStateAction<ReadonlyArray<string>>>;
  expressionsEditor: ChatBotExpressionsEditor;
};
